import React from 'react';
import { 
  FaTruck, 
  FaUsers, 
  FaRecycle, 
  FaGlobe,
  FaCheckCircle,
  FaStar
} from 'react-icons/fa';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">About Jhonks</h1>
            <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto">
              Transforming waste into value through innovative technology and sustainable practices
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Introduction */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Who We Are</h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Jhonks is a revolutionary platform that connects waste producers with material validators, 
            creating a sustainable ecosystem for waste management. We believe that every piece of waste 
            has value and can be transformed into something useful through proper collection, validation, 
            and processing.
          </p>
        </div>

        {/* Core Concepts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <FaTruck className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Material Collection</h3>
            <p className="text-gray-600">
              We facilitate the collection of various waste materials from sellers, ensuring proper 
              categorization and initial processing for maximum value recovery.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <FaUsers className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Agent Network</h3>
            <p className="text-gray-600">
              Our certified agents validate materials, ensuring quality and authenticity while 
              providing professional assessment and fair pricing for sellers.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <FaRecycle className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Waste Management</h3>
            <p className="text-gray-600">
              We implement comprehensive waste management strategies that prioritize recycling, 
              reuse, and responsible disposal of materials.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <FaGlobe className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Sustainability</h3>
            <p className="text-gray-600">
              Our platform promotes environmental sustainability by reducing waste in landfills 
              and creating circular economy opportunities.
            </p>
          </div>
        </div>

        {/* User Roles */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Platform Users</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FaUsers className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Sellers</h3>
              <p className="text-gray-600">
                Individuals and businesses who have waste materials to sell. They can list their 
                materials, get them validated by agents, and receive fair compensation.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Agents</h3>
              <p className="text-gray-600">
                Certified professionals who validate materials, assess quality, and ensure 
                proper categorization. They play a crucial role in maintaining platform integrity.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FaStar className="h-10 w-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Administrators</h3>
              <p className="text-gray-600">
                Platform managers who oversee operations, manage user accounts, and ensure 
                smooth functioning of the entire ecosystem.
              </p>
            </div>
          </div>
        </div>

        {/* Impact Section */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-lg text-white p-8 mb-16">
          <h2 className="text-3xl font-bold mb-6 text-center">Our Impact</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">1000+</div>
              <div className="text-green-100">Materials Processed</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">500+</div>
              <div className="text-green-100">Active Agents</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-green-100">Tons Recycled</div>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Transparency</h3>
              <p className="text-gray-600">We maintain complete transparency in all our transactions and processes.</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Sustainability</h3>
              <p className="text-gray-600">Environmental responsibility is at the core of everything we do.</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Innovation</h3>
              <p className="text-gray-600">We continuously innovate to improve our platform and services.</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fairness</h3>
              <p className="text-gray-600">We ensure fair compensation for all our users and partners.</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Quality</h3>
              <p className="text-gray-600">We maintain high standards in material validation and processing.</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Community</h3>
              <p className="text-gray-600">We build strong communities around sustainable practices.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
